import React from 'react'
import Authentication from '../common/Authentication'
import { Grid } from '@mui/material'
import { Helmet } from 'react-helmet'
import Header from '../common/ToolsHeader'
import MobileAppHeader from '../common/MobileAppHeader'
import { BANNER_HEIGHT_CSS } from '../common/BannerManager'
import { getIsMobileApp } from '../../utils/mobileApp'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Settings from './Settings'
import AvailablePlayers from './AvailablePlayers'
import Lineups from './Lineups'
import Matchups from './Matchups'
import { useMediaQuery } from '@material-ui/core'
import SimulationResults from './SimulationResults'
import { useDispatch, useSelector } from 'react-redux'
import { Lineup, LineupBuilderState } from './store/types'
import { IRootState } from '../../store/reducer'
import { setSavedLineups } from './store/actions'
import { useUserId, userSubscribed } from '../utils/authentication'
import { APIConstants } from '../utils/api/constants'
import LoginRegisterContainer from '../common/LoginRegisterContainer'
import Subscribe from '../common/Subscribe'
import { localStorageWithExpiry } from '../../utils/localStorageWithExpiry'
import { LineupApiResponse } from '../preContest/queries/queryTypes'
import { convertLineupToApiResponse, isLineupComplete } from './favoriteSnapshot'

const LineupBuilder = () => {
  const queryClient = new QueryClient();
  const { selectedLineupId, lineups } = useSelector<any, LineupBuilderState>((state) => state.lineupBuilder)
  const slateProjections = useSelector((state: IRootState) => state.lineupBuilder.slateProjections);
  const { authenticated, selectedSlate, selectedSport, selectedSite } = useSelector((state: IRootState) => state.common)
  const { simStatus } = lineups.find((x) => x.id === selectedLineupId) || {} as Lineup;
  const localStorageKey = `lineupBuilder-${selectedSport.sport}-${selectedSite.site}-${selectedSlate?.slateId}`
  const dispatch = useDispatch()
  const userId = useUserId();
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);
  const isAuthInProgress = authenticated.status === APIConstants.STARTED || authenticated.status === APIConstants.UNSTARTED;

  const matches = useMediaQuery('(min-width:1199px)');

  // Track when lineups were loaded for the current slate to prevent cross-contamination
  const lineupsLoadedForSlate = React.useRef<string | null>(null);

  // Clear expired items on component mount
  React.useEffect(() => {
    localStorageWithExpiry.clearExpired('lineupBuilder');
  }, []);

  React.useEffect(() => {
    // Only save lineups if they were loaded for the current slate context
    const currentSlateKey = `${selectedSport.sport}-${selectedSite.site}-${selectedSlate?.slateId}`;
    if (lineupsLoadedForSlate.current === currentSlateKey) {
      if (lineups.length === 0) {
        // Clear localStorage when all lineups are deleted
        localStorage.removeItem(localStorageKey);
      } else {
        // Save current lineup state (whether empty or with players)
        localStorageWithExpiry.setItem(localStorageKey, lineups, 336);
      }
    }
  }, [lineups, selectedSport.sport, selectedSite.site, selectedSlate?.slateId])

  React.useEffect(() => {
    // Favorites store a frozen snapshot of the lineup's players, which Entry
    // Manager uploads verbatim — refresh the snapshot whenever a favorited
    // lineup is edited so it never exports pre-edit players.
    const currentSlateKey = `${selectedSport.sport}-${selectedSite.site}-${selectedSlate?.slateId}`;
    if (!selectedSlate?.slateId || lineupsLoadedForSlate.current !== currentSlateKey) return;
    const favoritesKey = `lineupbuilder-favorites-${selectedSlate.slateId}`;
    try {
      const raw = localStorage.getItem(favoritesKey);
      if (!raw) return;
      const favorites: LineupApiResponse[] = JSON.parse(raw);
      if (!Array.isArray(favorites) || favorites.length === 0) return;
      let changed = false;
      const synced = favorites.map((favorite) => {
        const live = lineups.find((l) => l.id === favorite.id);
        if (!live || !isLineupComplete(live)) return favorite;
        const snapshot = convertLineupToApiResponse(live);
        if (snapshot.exportableLineup === favorite.exportableLineup) return favorite;
        changed = true;
        return { ...favorite, ...snapshot };
      });
      if (changed) {
        localStorage.setItem(favoritesKey, JSON.stringify(synced));
        // Notify useLocalStorage consumers (favorite hearts, Entry Manager)
        window.dispatchEvent(new Event('local-storage'));
      }
    } catch (error) {
      console.warn('Failed to sync favorited lineups with edits:', error);
    }
  }, [lineups, selectedSport.sport, selectedSite.site, selectedSlate?.slateId])

  React.useEffect(() => {
    if(selectedSport.sport && selectedSite.site && selectedSlate.slateId) {
      const currentSlateKey = `${selectedSport.sport}-${selectedSite.site}-${selectedSlate.slateId}`;
      const savedLineups = localStorageWithExpiry.getItem(localStorageKey);
      if(savedLineups) {
        // Drop saved lineups whose slots no longer match the slate's roster header (e.g. after a header change).
        const header = selectedSlate.lineupRules?.positions;
        const compatible = header
          ? savedLineups.filter((lineup: Lineup) => lineup.players.map((p) => p.position).join(',') === header)
          : savedLineups;
        dispatch(setSavedLineups(compatible, slateProjections))
      } else {
        dispatch(setSavedLineups([]))
      }
      // Mark that lineups have been loaded for this slate
      lineupsLoadedForSlate.current = currentSlateKey;
    }
  }, [selectedSlate, selectedSport, selectedSite])

  return (
    <QueryClientProvider client={queryClient}>
      <Authentication app='SINGLELINEUPSIM'>
        <Helmet>
          <title>Lineup Builder</title>
          <meta name="description" content="Stokastic Lineup Builder" />
        </Helmet>
        <Grid sx={{
          // Subtract the app-level promo banner so this fixed-height layout
          // doesn't push its bottom controls off-screen while a banner is showing.
          minHeight: getIsMobileApp() ? `calc(100vh - 64px - ${BANNER_HEIGHT_CSS})` : { xs: `calc(100dvh - ${BANNER_HEIGHT_CSS})`, lg: `calc(100vh - ${BANNER_HEIGHT_CSS})` },
          maxHeight: getIsMobileApp() ? `calc(100vh - 64px - ${BANNER_HEIGHT_CSS})` : { xs: `calc(100dvh - ${BANNER_HEIGHT_CSS})`, lg: `calc(100vh - ${BANNER_HEIGHT_CSS})` },
          backgroundColor: getIsMobileApp() ? '#0D0E10' : "#0A0A0A",
          color: (theme) => theme.palette.common.white,
          fontFamily: "Roboto",
          display: "flex",
          flexDirection: "column",
          overflow: { xs: 'hidden', lg: 'scroll' },
          overscrollBehavior: { xs: 'none', lg: 'auto' },
          height: '100%',
          '& div': {
            scrollbarColor: "#6b6b6b #2b2b2b",
            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                backgroundColor: "#2b2b2b",
            },
            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                borderRadius: 8,
                backgroundColor: "#6b6b6b",
                minHeight: 24,
                border: "3px solid #2b2b2b",
            },
            "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
                backgroundColor: "#959595",
            },
            "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
                backgroundColor: "#959595",
            },
            "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#959595",
            },
            "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
                backgroundColor: "#2b2b2b",
            },
          }
          
        }}>
          {!getIsMobileApp() && <Grid item xs={12}><Header /></Grid>}
          {!(selectedLineupId && !matches) && <MobileAppHeader title="Single-Lineup Sim" />}
          {
            userId ? (
              <>
                <Grid
                  container
                  item
                  xs={12}
                  direction="column"
                  flexGrow={{ xs: 0, lg: 0 }}
                  sx={selectedLineupId && !matches ? { display: 'none' } : undefined}
                >
                  <Grid item xs={12}>
                    <Settings />
                  </Grid>
                  {
                    isUserSubscribed &&  Object.keys(selectedSlate).length > 0 && (
                      <Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }} width='100%'>
                        <Matchups />
                      </Grid>

                    )
                  }
                </Grid>
                {
                  isAuthInProgress ? (
                    null
                  ) : !isUserSubscribed ? (
                    <Subscribe toolName='Single-Lineup Simulator' />
                  ) : Object.keys(selectedSlate).length > 0 ? (
                      <Grid container item xs={12} justifyContent="center" padding={{ xs: 0, lg: '20px' }} rowGap={'10px'} sx={{ flexGrow: '1 !important', flex: { xs: '1 1 0% !important', lg: undefined }, flexDirection: { xs: 'column', lg: 'row' }, flexWrap: { xs: 'nowrap', lg: 'wrap' }, overflow: { xs: 'hidden', lg: 'visible' }, minHeight: { xs: 0, lg: 'auto' } }}>
                        <Grid item xs={12} lg={5.4} pr={{ xs: 0, lg: '15px' }} sx={{ display: { xs: 'flex', lg: 'block' }, flexDirection: 'column', flex: { xs: 1 }, overflow: 'hidden', minHeight: 0, maxHeight: { lg: 'calc(100vh - 270px)' } }}>
                          <Lineups />
                        </Grid>
                        {
                          matches && (
                            <Grid item xs={12} lg={6.6} pl={{ xs: 0, lg: '15px' }} sx={{ display: { xs: 'none', lg: 'block' } }}>
                              {
                                selectedLineupId && simStatus === 'complete' ? <SimulationResults /> : <AvailablePlayers />
                              }
                            </Grid>
                          )
                        }
                      </Grid>
                    ) : (
                      <Grid container item xs={12} justifyContent="center" alignItems="center" sx={{ flexGrow: '1 !important' }}>
                        <Grid item sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', px: 4 }}>
                          {`No slate upcoming for ${selectedSport?.sport || ''}. The Single-Lineup Simulator can only be used within ${selectedSport?.sport === 'PGA' ? 48 : 12} hours of a slate's start time and once projections have been released. Please select a different sport, or come back closer to lock.`}
                        </Grid>
                      </Grid>
                    )
                }
              </>
            ) : <LoginRegisterContainer toolName='Single-Lineup Simulator' />
          }
        </Grid>
      </Authentication>
    </QueryClientProvider>
  )
}

export default LineupBuilder
